import { z } from "zod";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import { useTranslation } from "react-i18next";
import { ControlledDatepicker, ControlledInput } from "../../../modules/form";
import {
  FunnelStepBackControl,
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import {
  getArrivalAirport,
  getDepartureAirport,
  updateClaimDetails,
  updateItineraryFlightByIndex,
  usePreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import { Box, Center, Flex, Spinner, Text } from "@chakra-ui/react";
import { useForm, useFormHandlers } from "v2/modules/form";
import { sendWarningReport } from "utils/reporting";
import { useFunnel } from "@airhelp/funnel";
import { useFetchFlights } from "v2/modules/hooks/useFetchFlights";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess, trackFutureFlightSelected } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";
import { addDays } from "date-fns";
import { SUPPORTED_FUTURE_DAYS } from "../../../settings/constants";
import { useEffect, useMemo, useRef } from "react";
import { ControlledAirlineAutocomplete } from "v2/modules/form/ControlledAirlineAutocomplete";
import { ControlledFlightSelector } from "v2/modules/form/ControlledFlightSelector";
import { airlineSchema } from "./MultilegDisruptedFlight";
import { getAirlineCode, sanitizeFlightNumber } from "v2/modules/form/ItineraryDetailsControl";
import { FlightSelectorFlight } from "v2/modules/types/intake-funnel";
import scrollToRef from "utils/scrollToRef";
import {
  extractAirlineFromFSFlight,
  filterByAirlineId,
  isCantFindFlightChecked,
} from "v2/utils/flightSelectorHelpers";
import { fetchPotentialRegulation } from "v2/api/potential-regulation";
import { InputGroup } from "v2/components/funnel/forms/InputGroup";
import { AirplaneIcon } from "v2/components/funnel/ui-elements/airplane-icon/Airplane";
import { flightNumberRegexp } from "validators/zod";

const defaultStepSchema = z.object({
  flightDate: z.date().nullable(),
  airline: airlineSchema.nullable(),
  flightNumberSelectValue: z.string().nullable(),
  flightNumberInputValue: z.string().nullable(),
});

export function FlightSelectorStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { itineraryFlights } = usePreEligibilityDataSnapshot();
  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });

  const arrivalAirport = getArrivalAirport();
  const departureAirport = getDepartureAirport();

  const bottomRef = useRef(null);
  const airlineSectionRef = useRef(null);
  const flightSelectorSectionRef = useRef(null);

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    async onSubmit({ flightDate, airline, flightNumberSelectValue, flightNumberInputValue }) {
      if (flightDate !== null) {
        const selectedFlight: FlightSelectorFlight | undefined =
          flightNumberSelectValue && !isCantFindFlightChecked(flightNumberSelectValue)
            ? filteredFlights?.find(
                (flight) =>
                  flight.airlineIdentifier + flight.airlineCode + flight.flightNumber ===
                  flightNumberSelectValue,
              )
            : undefined;

        let finalAirline = airline;

        if (selectedFlight) {
          finalAirline = extractAirlineFromFSFlight(selectedFlight);
        }

        const updatedItineraryFlights = updateItineraryFlightByIndex(0, {
          flightDate,
          airline: finalAirline,
          flightNumber: selectedFlight ? selectedFlight.flightNumber : flightNumberInputValue,
          flightSource: selectedFlight ? "flight_selector" : "manual",
        });

        try {
          const potentialRegulation = await fetchPotentialRegulation(updatedItineraryFlights);
          updateClaimDetails({
            potentialRegulation,
            potentialClaimType: selectedFlight?.humanizedArrivalStatus,
          });
        } catch (e) {
          updateClaimDetails({
            potentialRegulation: "ec261",
            potentialClaimType: selectedFlight?.humanizedArrivalStatus,
          });
          sendWarningReport(
            "Failed to fetch potential regulation, `ec261` was used as fallback",
            e,
          );
        }
      } else {
        sendWarningReport("Missing date of flight on submit of flight selector date step");
      }
      trackFutureFlightSelected(flightDate);
      trackClaimProcess({ funnelStep: funnel.currentStep?.name });
      trackEnquiryStepComplete();

      funnel.stepForward();
    },
  });

  const stepSchema = z
    .object({
      flightDate: z.date(),
      airline: airlineSchema.nullable(),
      flightNumberSelectValue: z.string().nullable(),
      flightNumberInputValue: z.string().nullable(),
    })
    .refine(
      ({ flightNumberInputValue }) => {
        if (flightNumberInputValue) {
          return flightNumberRegexp.test(flightNumberInputValue);
        }
        return true;
      },
      { message: "validation.flight_number.invalid", path: ["flightNumberInputValue"] },
    )
    .refine(({ airline, flightNumberInputValue, flightNumberSelectValue }) => {
      const isFlightSelected =
        flightNumberSelectValue && !isCantFindFlightChecked(flightNumberSelectValue);
      if ((isFlightSelected || flightNumberInputValue) && airline) {
        return true;
      }
      return false;
    });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      flightDate: itineraryFlights[0].flightDate,
      airline: itineraryFlights[0].airline,
      flightNumberSelectValue: "",
      flightNumberInputValue: itineraryFlights[0]?.flightNumber ?? "",
    },
  });

  const flightDate = watch("flightDate");
  const airline = watch("airline");
  const flightNumberSelectValue = watch("flightNumberSelectValue");
  const flightNumberInputValue = watch("flightNumberInputValue");

  const { flights, isFetching, refetch } = useFetchFlights(
    flightDate,
    departureAirport?.iata,
    arrivalAirport?.iata,
    false,
  );

  const filteredFlights = useMemo(() => {
    if (!flights?.length || !airline) {
      return [];
    }
    return filterByAirlineId(flights, airline.uniqueIdentifier);
  }, [flights, airline]);

  useEffect(() => {
    if (filteredFlights.length === 1 && !flightNumberSelectValue && !flightNumberInputValue) {
      setValue(
        "flightNumberSelectValue",
        filteredFlights[0].airlineIdentifier +
          filteredFlights[0].airlineCode +
          filteredFlights[0].flightNumber,
        { shouldValidate: true },
      );
    }
  }, [
    filteredFlights.length,
    filteredFlights,
    setValue,
    flightNumberSelectValue,
    flightNumberInputValue,
  ]);

  const showAirlineSection = flightDate;
  const showSpinner = isFetching && flightDate && airline;
  const showFlightSelector = flightDate && !isFetching && filteredFlights?.length && airline;
  const showFlightNumberInput =
    flightDate &&
    !isFetching &&
    (!filteredFlights?.length || isCantFindFlightChecked(flightNumberSelectValue)) &&
    airline;

  return (
    <FunnelStepContainer data-testid="flightSelectorDateStep">
      <Flex alignItems="center">
        <Text
          as="span"
          fontSize="md"
        >{`${departureAirport?.city} (${departureAirport?.iata})`}</Text>
        <AirplaneIcon color="greyscale.500" m="0 8px" />
        <Text as="span" fontSize="md">{`${arrivalAirport?.city} (${arrivalAirport?.iata})`}</Text>
      </Flex>

      <FunnelStepSection title={t("steps.flight_selector_date.text")}>
        <Box w={{ base: "100%", md: "75%" }} data-sentry-unmask>
          <ControlledDatepicker
            name={"flightDate"}
            label={t("steps.flight_selector_date.label")}
            placeholder={t("steps.ec261.personal_details.date_of_birth.placeholder")}
            maxDate={addDays(new Date(), SUPPORTED_FUTURE_DAYS)}
            handleChange={async (newDate) => {
              if (newDate) {
                setValue("flightNumberInputValue", "", { shouldValidate: true });
                setValue("flightNumberSelectValue", "", { shouldValidate: true });
                // avoid state updates batching or using useEffect
                Promise.resolve().then(() => {
                  refetch();
                });
                scrollToRef(airlineSectionRef);
              }
            }}
            control={control}
          />
        </Box>
      </FunnelStepSection>

      <span ref={airlineSectionRef} />

      {showAirlineSection && (
        <FunnelStepSection title={t("steps.itinerary_details.airline_question")}>
          <InputGroup>
            <ControlledAirlineAutocomplete
              control={control}
              dataTestId="airlineAutocomplete"
              name="airline"
              label={t("steps.itinerary_details.airline")}
              placeholder={t("steps.itinerary_details.airline_placeholder")}
              handleChange={async (newAirline) => {
                if (newAirline) {
                  setValue("flightNumberInputValue", "");
                  setValue("flightNumberSelectValue", "");
                  scrollToRef(flightSelectorSectionRef);
                }
              }}
            />
          </InputGroup>
        </FunnelStepSection>
      )}

      {showSpinner && (
        <Center p="6">
          <Spinner variant="contentMedium" />
        </Center>
      )}
      <span ref={flightSelectorSectionRef} />

      {showFlightSelector && (
        <FunnelStepSection title={t("steps.flights_list.please_choose")}>
          <InputGroup>
            <ControlledFlightSelector
              onChange={() => {
                setValue("flightNumberInputValue", "");
                scrollToRef(bottomRef);
              }}
              control={control}
              name="flightNumberSelectValue"
              flights={filteredFlights}
            />
          </InputGroup>
          <span ref={bottomRef} />
        </FunnelStepSection>
      )}

      {showFlightNumberInput && (
        <FunnelStepSection title={t("steps.itinerary_details.flight_number_question")}>
          <ControlledInput
            isDouble
            control={control}
            data-testid="flightNumber"
            leftAddonText={getAirlineCode(airline) || ""}
            name="flightNumberInputValue"
            placeholder={"1234"}
            label={t("steps.itinerary_details.flight_number")}
            sx={{ w: "100%" }}
            maxLength={5}
            minWidth="78px"
            sanitizeInput={sanitizeFlightNumber}
          />
        </FunnelStepSection>
      )}

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepBackControl />
        <FunnelStepForwardControl
          isDisabled={!isValid || isFetching}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}
